import React, { useState, useEffect } from 'react';
import {
    List,
    Datagrid,
    TextField,
    TopToolbar,
    useNotify,
    useRefresh,
    useRecordContext,
    Button,
    useListContext,
    useDataProvider
} from 'react-admin';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions
} from '@mui/material';
import { apiUrl } from '../../config/api';
import { httpClient } from '../../utils/httpClient';

const WaitListActions = () => (
    <TopToolbar>
    </TopToolbar>
);

const ApproveButton = () => {
    const record = useRecordContext();
    const notify = useNotify();
    const refresh = useRefresh();
    const { selectedIds, onUnselectItems } = useListContext();
    const [open, setOpen] = useState(false);

    const handleApprove = async () => {
        if (!record) return;

        setOpen(false);
        
        try {
            const response = await httpClient(`${apiUrl}/admin/waitlist/approve`, {
                method: 'POST',
                body: JSON.stringify({
                    emails: [record.email]
                })
            });

            if (response.json.success) {
                notify(`User ${record.email} approved successfully`, { type: 'success' });
                // Clear selection if this record was selected
                if (onUnselectItems && selectedIds.includes(record.id)) {
                    onUnselectItems();
                }
                refresh();
            } else {
                notify(response.json.message || 'Failed to approve user', { type: 'error' });
            }
        } catch (error: any) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to approve user';
            notify(errorMessage, { type: 'error' });
        }
    };

    return (
        <>
            <Button
                label="Give Access"
                onClick={() => setOpen(true)}
                sx={{ color: '#1976d2' }}
            />
            <Dialog open={open} onClose={() => setOpen(false)}>
                <DialogTitle>Confirm Approval</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to give access to {record?.email}? This action will send an approval email and add them to the whitelist.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)} color="primary">
                        No
                    </Button>
                    <Button onClick={handleApprove} color="primary" variant="contained" autoFocus>
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

const BulkApproveButton = () => {
    const { selectedIds, data, onUnselectItems } = useListContext();
    const notify = useNotify();
    const refresh = useRefresh();
    const [open, setOpen] = useState(false);
    const [emailsToApprove, setEmailsToApprove] = useState<string[]>([]);
    const [selectedCount, setSelectedCount] = useState(0);

    // Update selected count when selectedIds or data changes
    useEffect(() => {
        if (!data || selectedIds.length === 0) {
            setSelectedCount(0);
            return;
        }

        // Filter to only count records that actually exist in current data
        const validSelectedRecords = data.filter((record: any) => selectedIds.includes(record.id));
        setSelectedCount(validSelectedRecords.length);
    }, [selectedIds, data]);

    const handleClick = () => {
        if (selectedIds.length === 0) {
            notify('Please select at least one user', { type: 'warning' });
            return;
        }

        if (!data || data.length === 0) {
            notify('No data available', { type: 'error' });
            return;
        }

        // Get emails from selected records that actually exist in current data
        const selectedRecords = data.filter((record: any) => selectedIds.includes(record.id));
        const emails = selectedRecords.map((record: any) => record.email).filter(Boolean);

        if (emails.length === 0) {
            notify('No valid emails found', { type: 'error' });
            return;
        }

        setEmailsToApprove(emails);
        setOpen(true);
    };

    const handleBulkApprove = async () => {
        setOpen(false);

        try {
            const response = await httpClient(`${apiUrl}/admin/waitlist/approve`, {
                method: 'POST',
                body: JSON.stringify({ emails: emailsToApprove })
            });

            if (response.json.success) {
                notify(`Successfully approved ${emailsToApprove.length} user(s)`, { type: 'success' });
                // Clear selection before refresh
                if (onUnselectItems && selectedIds.length > 0) {
                    onUnselectItems();
                }
                setEmailsToApprove([]);
                refresh();
            } else {
                notify(response.json.message || 'Failed to approve users', { type: 'error' });
            }
        } catch (error: any) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to approve users';
            notify(errorMessage, { type: 'error' });
        }
    };

    return (
        <>
            <Button
                label={selectedCount > 0 ? `Give Access (${selectedCount})` : 'Give Access'}
                onClick={handleClick}
                disabled={selectedCount === 0}
                sx={{ color: '#1976d2' }}
            />
            <Dialog open={open} onClose={() => setOpen(false)}>
                <DialogTitle>Confirm Approval</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to give access to {emailsToApprove.length} selected user(s)? This action will send approval emails and add them to the whitelist.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)} color="primary">
                        No
                    </Button>
                    <Button onClick={handleBulkApprove} color="primary" variant="contained" autoFocus>
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export const WaitList = () => (
    <List
        actions={<WaitListActions />}
    >
        <Datagrid rowClick="show" bulkActionButtons={<BulkApproveButton />}>
            <TextField source="name" label="Name" />
            <TextField source="email" label="Email" />
            <ApproveButton />
        </Datagrid>
    </List>
);

